import React, { useState, useEffect } from 'react'
import { XMarkIcon, Cog6ToothIcon } from '@heroicons/react/24/solid'
import extensionService from '../../../services/extensionService'
import { trackError } from '../../../services/httpService'
import { EXTENSION_CONFIG } from '../../../config/extensionConfig'
import MilesSection from './MilesSection'
import AlertMessage from './AlertMessage'
import RouteSection from './RouteSection'
import MessagesSection from './MessagesSection'
import PostATruckSettings from './PostATruckSettings'
import './PostATruckModal.css'
import {
	transformLoadForExtension,
	formatCity,
	formatTime,
	getTrailerDetails,
} from './postTruckHelpers'

const POST_TRUCK_SETTINGS_KEY = 'postATruckSettings'

const getDefaultSettings = () => ({
	mileBuffer: 25,
	payoutOffset: -1,
	perMileOffset: -0.01,
	originRadius: 25,
	destinationRadius: 50,
	stemTime: '30',
	originDateStep: '-30',
	destinationDateStep: '30',
})

const loadSavedSettings = () => {
	try {
		const saved = localStorage.getItem(POST_TRUCK_SETTINGS_KEY)
		if (saved) {
			return { ...getDefaultSettings(), ...JSON.parse(saved) }
		}
	} catch (error) {
		console.error('Error loading post truck settings:', error)
	}
	return getDefaultSettings()
}

const saveSettings = (settings) => {
	try {
		localStorage.setItem(POST_TRUCK_SETTINGS_KEY, JSON.stringify(settings))
	} catch (error) {
		console.error('Error saving post truck settings:', error)
	}
}

function PostATruckModal({ isOpen, onClose, load, isDarkMode }) {
	const [isConfirming, setIsConfirming] = useState(false)
	const [extensionMessage, setExtensionMessage] = useState(null)
	const [isAnimating, setIsAnimating] = useState(false)
	const [showSettings, setShowSettings] = useState(false)
	const [settings, setSettings] = useState(getDefaultSettings())
	const settingsRef = React.useRef(null)

	const [perMileOffsetInput, setPerMileOffsetInput] = useState('')
	const [payoutOffsetInput, setPayoutOffsetInput] = useState('')
	const [mileBufferInput, setMileBufferInput] = useState('')

	const [adjustedPrice, setAdjustedPrice] = useState('')
	const [adjustedPricePerMile, setAdjustedPricePerMile] = useState('')
	const [lastEditedField, setLastEditedField] = useState(null)

	const [minMiles, setMinMiles] = useState('')
	const [maxMiles, setMaxMiles] = useState('')
	const [originRadius, setOriginRadius] = useState(25)
	const [destinationRadius, setDestinationRadius] = useState(50)
	const [stemTime, setStemTime] = useState('30')
	const [updatedPickupTime, setUpdatedPickupTime] = useState(null)
	const [updatedDeliveryTime, setUpdatedDeliveryTime] = useState(null)
	const [pickupWarning, setPickupWarning] = useState(false)
	const [deliveryWarning, setDeliveryWarning] = useState(false)
	const [datesAdjusted, setDatesAdjusted] = useState(false)

	// Animation handling
	useEffect(() => {
		if (isOpen) {
			// Start with modal hidden, then trigger animation
			setIsAnimating(false)
			requestAnimationFrame(() => {
				setIsAnimating(true)
			})
		} else {
			// Immediately start closing animation
			setIsAnimating(false)
		}
	}, [isOpen])

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (settingsRef.current && !settingsRef.current.contains(event.target) && showSettings) {
				const settingsButton = event.target.closest('button[title="Settings"]')
				if (!settingsButton) {
					setShowSettings(false)
				}
			}
		}

		if (showSettings) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [showSettings])

	useEffect(() => {
		if (isOpen && load) {
			setExtensionMessage(null)
			setIsConfirming(false)
			setShowSettings(false)

			const loadedSettings = loadSavedSettings()
			setSettings(loadedSettings)

			setPerMileOffsetInput(String(loadedSettings.perMileOffset))
			setPayoutOffsetInput(String(loadedSettings.payoutOffset))
			setMileBufferInput(String(loadedSettings.mileBuffer))

			const totalDistance = load.distance || 0

			const initialPrice = (load.price || 0) + loadedSettings.payoutOffset
			setAdjustedPrice(initialPrice.toFixed(2))

			const loadPricePerMile = (load.price || 0) / (totalDistance || 1)
			const initialPricePerMile = loadPricePerMile + loadedSettings.perMileOffset
			setAdjustedPricePerMile(initialPricePerMile.toFixed(2))
			setLastEditedField(null)

			setMinMiles(Math.max(1, Math.round(totalDistance - loadedSettings.mileBuffer)).toString())
			setMaxMiles(Math.round(totalDistance + loadedSettings.mileBuffer).toString())

			setOriginRadius(loadedSettings.originRadius)
			setDestinationRadius(loadedSettings.destinationRadius)

			setStemTime(loadedSettings.stemTime)

			const now = new Date()
			const originalPickupTime = new Date(
				load.pickup?.date_local || new Date().toISOString()
			)
			const originalDeliveryTime = new Date(
				load.dropOff?.date_local || new Date().toISOString()
			)

			let adjustedPickupTime, adjustedDeliveryTime
			const wasAdjusted = originalPickupTime.getTime() < now.getTime()

			if (wasAdjusted) {
				adjustedPickupTime = new Date(now.getTime() + 1.5 * 60 * 60 * 1000)

				const originalTransitTime =
					originalDeliveryTime.getTime() - originalPickupTime.getTime()
				const minTransitTime = 4 * 60 * 60 * 1000
				const transitTime = Math.max(originalTransitTime, minTransitTime)

				adjustedDeliveryTime = new Date(
					adjustedPickupTime.getTime() + transitTime + 3 * 60 * 60 * 1000
				)
			} else {
				const originOffsetMs = Math.abs(parseInt(loadedSettings.originDateStep) || 30) * 60 * 1000
				const destOffsetMs = Math.abs(parseInt(loadedSettings.destinationDateStep) || 30) * 60 * 1000
				adjustedPickupTime = new Date(
					originalPickupTime.getTime() - originOffsetMs
				)
				adjustedDeliveryTime = new Date(
					originalDeliveryTime.getTime() + destOffsetMs
				)
			}

			setUpdatedPickupTime(adjustedPickupTime)
			setUpdatedDeliveryTime(adjustedDeliveryTime)
			setDatesAdjusted(wasAdjusted)
		}
	}, [isOpen, load])


	if (!isOpen || !load) return null

	const { pickup, dropOff, loadingType } = load
	const trailerDetails = getTrailerDetails(load)

	const handlePriceChange = value => {
		setAdjustedPrice(value)
		setLastEditedField('price')
	}

	const handlePricePerMileChange = value => {
		setAdjustedPricePerMile(value)
		setLastEditedField('pricePerMile')
		// Calculate payout based on per mile and distance
		if (value && !isNaN(parseFloat(value))) {
			const totalDistance = load?.distance || 1
			const calculatedPayout = parseFloat(value) * totalDistance
			setAdjustedPrice(calculatedPayout.toFixed(2))
		}
	}

	const updatePickupTime = delta => {
		if (!updatedPickupTime) return
		const newPickupTime = new Date(updatedPickupTime.getTime())
		const stepMinutes = parseInt(settings.originDateStep) || 30
		newPickupTime.setMinutes(newPickupTime.getMinutes() - delta * stepMinutes)
		setUpdatedPickupTime(newPickupTime)

		const originalPickupTime = new Date(
			load.pickup?.date_local || new Date().toISOString()
		)
		setPickupWarning(newPickupTime.getTime() >= originalPickupTime.getTime())
	}

	const updateDeliveryTime = delta => {
		if (!updatedDeliveryTime) return
		const newDeliveryTime = new Date(updatedDeliveryTime.getTime())
		const stepMinutes = parseInt(settings.destinationDateStep) || 30
		newDeliveryTime.setMinutes(newDeliveryTime.getMinutes() + delta * stepMinutes)
		setUpdatedDeliveryTime(newDeliveryTime)

		const originalDeliveryTime = new Date(
			load.dropOff?.date_local || new Date().toISOString()
		)
		setDeliveryWarning(
			newDeliveryTime.getTime() <= originalDeliveryTime.getTime()
		)
	}

	const handleInputChange = (field, value) => {
		switch (field) {
			case 'minMiles':
				setMinMiles(Math.max(1, value).toString())
				break
			case 'maxMiles':
				setMaxMiles(value.toString())
				break
			case 'stemTime':
				setStemTime(value)
				break
			case 'originRadius':
				setOriginRadius(value)
				break
			case 'destinationRadius':
				setDestinationRadius(value)
				break
			default:
				break
		}
	}

	const validatePriceInputs = () => {
		const price = parseFloat(adjustedPrice)
		const pricePerMile = parseFloat(adjustedPricePerMile)

		if (isNaN(price) || price <= 0) return 'Please enter a valid price'
		if (isNaN(pricePerMile) || pricePerMile <= 0)
			return 'Please enter a valid price per mile'
		if (price < 0) return 'Price cannot be negative'
		if (pricePerMile < 0) return 'Price per mile cannot be negative'
		if (price > 50000) return 'Price seems too high (max $50,000)'
		if (pricePerMile > 100)
			return 'Price per mile seems too high (max $100/mile)'
		return null
	}

	const handleConfirm = async () => {
		const priceValidationError = validatePriceInputs()
		if (priceValidationError) {
			setExtensionMessage({
				type: 'error',
				title: 'Invalid Price',
				message: priceValidationError,
			})
			return
		}

		setIsConfirming(true)
		setExtensionMessage(null)

		try {
			const compatibility = await extensionService.checkExtensionCompatibility()

			if (!compatibility.compatible) {
				// Track extension compatibility error
				const compatibilityError = new Error(
					`${compatibility.message} | diagnostics ${JSON.stringify(compatibility.diagnostics)}`
				)
				await trackError(compatibilityError, 'post truck', 'website')

				setExtensionMessage({
					type: 'error',
					title:
						compatibility.action === 'install'
							? `${EXTENSION_CONFIG.EXTENSION_NAME} Not Installed`
							: `${EXTENSION_CONFIG.EXTENSION_NAME} Update Required`,
					message: compatibility.message,
					action: compatibility.action,
					actionUrl: EXTENSION_CONFIG.STORE_URL,
				})
				setIsConfirming(false)
				return
			}

			const transformedLoadData = transformLoadForExtension(load, {
				adjustedPrice,
				adjustedPricePerMile,
				minMiles,
				maxMiles,
				stemTime,
				originRadius,
				destinationRadius,
				updatedPickupTime,
				updatedDeliveryTime,
			})

			const result = await extensionService.postTruck(transformedLoadData)

			if (result.success) {
				setExtensionMessage({
					type: 'success',
					message: 'Post A Truck order has been posted.',
				})
				setTimeout(() => {
					onClose()
				}, 3000)
			} else {
				// Track post truck failure
				const postError = new Error(result.error || 'Failed to post truck')
				await trackError(postError, 'post truck', 'website')

				setExtensionMessage({
					type: 'error',
					title: 'Failed to Post Truck',
					message: result.error || 'An unexpected error occurred.',
				})
			}
		} catch (error) {
			console.error('Post truck error:', error)

			// Track error in OpenSearch
			await trackError(error, 'post truck', 'website')

			if (
				error.isTimeout ||
				error.message.includes('timeout') ||
				error.message.includes('took too long')
			) {
				setExtensionMessage({
					type: 'error',
					title: 'Extension Not Responding',
					message:
						'The extension took too long to respond. Please try reloading the extension or updating to the latest version.',
					action: 'update',
					actionUrl: EXTENSION_CONFIG.STORE_URL,
				})
			} else if (
				error.message &&
				(error.message.includes('receiving end does not exist') ||
				 error.message.includes('Receiving end does not exist'))
			) {
				setExtensionMessage({
					type: 'error',
					title: 'Extension Installation Error',
					message:
						'Extension installation issue detected. Please restart your PC to ensure the new version of the extension gets installed to all pages, then try again.',
				})
			} else {
				setExtensionMessage({
					type: 'error',
					title: 'Communication Error',
					message:
						error.message ||
						'Failed to communicate with the extension. Please try again.',
				})
			}
		} finally {
			setIsConfirming(false)
		}
	}

	const dismissMessage = () => {
		setExtensionMessage(null)
	}

	const handleSaveSettings = (newSettings) => {
		saveSettings(newSettings)

		setSettings(newSettings)

		setPerMileOffsetInput(String(newSettings.perMileOffset))
		setPayoutOffsetInput(String(newSettings.payoutOffset))
		setMileBufferInput(String(newSettings.mileBuffer))

		if (load) {
			const totalDistance = load.distance || 0

			const newPrice = (load.price || 0) + newSettings.payoutOffset
			setAdjustedPrice(newPrice.toFixed(2))

			const loadPricePerMile = (load.price || 0) / (totalDistance || 1)
			const newPricePerMile = loadPricePerMile + newSettings.perMileOffset
			setAdjustedPricePerMile(newPricePerMile.toFixed(2))

			setMinMiles(Math.max(1, Math.round(totalDistance - newSettings.mileBuffer)).toString())
			setMaxMiles(Math.round(totalDistance + newSettings.mileBuffer).toString())

			setOriginRadius(newSettings.originRadius)
			setDestinationRadius(newSettings.destinationRadius)

			setStemTime(newSettings.stemTime)

			const now = new Date()
			const originalPickupTime = new Date(load.pickup?.date_local || new Date().toISOString())
			const originalDeliveryTime = new Date(load.dropOff?.date_local || new Date().toISOString())

			if (originalPickupTime.getTime() >= now.getTime()) {
				const originOffsetMs = Math.abs(parseInt(newSettings.originDateStep) || 30) * 60 * 1000
				const destOffsetMs = Math.abs(parseInt(newSettings.destinationDateStep) || 30) * 60 * 1000
				setUpdatedPickupTime(new Date(originalPickupTime.getTime() - originOffsetMs))
				setUpdatedDeliveryTime(new Date(originalDeliveryTime.getTime() + destOffsetMs))
			}
		}
	}

	const handleResetSettings = () => {
		const defaultSettings = getDefaultSettings()
		handleSaveSettings(defaultSettings)
	}

	const handleAnimatedClose = () => {
		setIsAnimating(false)
		setTimeout(() => {
			onClose()
		}, 200)
	}

	const handleOverlayClick = e => {
		if (e.target === e.currentTarget) {
			handleAnimatedClose()
		}
	}

	if (!isOpen) return null

	return (
		<div
			className={`modal-overlay ${isAnimating ? 'active' : ''}`}
			onClick={handleOverlayClick}
		>
			<div
				className={`modal-content ${isDarkMode ? 'dark' : ''} ${
					isAnimating ? 'active' : ''
				}`}
				onClick={e => e.stopPropagation()}
			>
				<div className={`modal-header ${isDarkMode ? 'dark' : ''}`}>
					<div className="flex items-center gap-2">
						<h2
							className={`text-md font-semibold ${
								isDarkMode ? 'text-gray-300' : 'text-gray-700'
							}`}
						>
							Post A Truck
						</h2>
						<div className="relative">
							<button
								onClick={() => setShowSettings(!showSettings)}
								className={`p-1.5 rounded transition-colors ${
									isDarkMode
										? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
										: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
								}`}
								title="Settings"
								disabled={isConfirming}
							>
								<Cog6ToothIcon className="h-4 w-4" />
							</button>
							<PostATruckSettings
								ref={settingsRef}
								showSettings={showSettings}
								isDarkMode={isDarkMode}
								settings={settings}
								perMileOffsetInput={perMileOffsetInput}
								payoutOffsetInput={payoutOffsetInput}
								mileBufferInput={mileBufferInput}
								setPerMileOffsetInput={setPerMileOffsetInput}
								setPayoutOffsetInput={setPayoutOffsetInput}
								setMileBufferInput={setMileBufferInput}
								setSettings={setSettings}
								handleSaveSettings={handleSaveSettings}
								handleResetSettings={handleResetSettings}
								onClose={() => setShowSettings(false)}
							/>
						</div>
					</div>
					<button
						onClick={handleAnimatedClose}
						disabled={isConfirming}
						className={`modal-close-btn ${isDarkMode ? 'dark' : ''}`}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<div className="modal-body">
					<AlertMessage
						message={extensionMessage}
						onDismiss={dismissMessage}
						isDarkMode={isDarkMode}
					/>

					<p
						className={`text-sm mb-2 -mt-2 ${
							isDarkMode ? 'text-gray-400' : 'text-gray-600'
						}`}
					>
						Are you sure you want to create the following order?
					</p>

					<MessagesSection
						datesAdjusted={datesAdjusted}
						pickupWarning={pickupWarning}
						deliveryWarning={deliveryWarning}
						isDarkMode={isDarkMode}
					/>

					{/* Route Section */}
					<RouteSection
						pickup={pickup}
						dropOff={dropOff}
						originRadius={originRadius}
						destinationRadius={destinationRadius}
						updatedPickupTime={updatedPickupTime}
						updatedDeliveryTime={updatedDeliveryTime}
						onOriginRadiusChange={e =>
							handleInputChange('originRadius', parseInt(e.target.value))
						}
						onDestinationRadiusChange={e =>
							handleInputChange('destinationRadius', parseInt(e.target.value))
						}
						onUpdatePickupTime={updatePickupTime}
						onUpdateDeliveryTime={updateDeliveryTime}
						formatCity={formatCity}
						formatTime={formatTime}
						disabled={isConfirming}
						isDarkMode={isDarkMode}
					/>

					{/* Combined Stats & Pricing Section */}
					<MilesSection
						minMiles={minMiles}
						maxMiles={maxMiles}
						handleInputChange={handleInputChange}
						isConfirming={isConfirming}
						isDarkMode={isDarkMode}
						load={load}
						adjustedPricePerMile={adjustedPricePerMile}
						adjustedPrice={adjustedPrice}
						stemTime={stemTime}
						lastEditedField={lastEditedField}
						onPricePerMileChange={handlePricePerMileChange}
						onPriceChange={handlePriceChange}
						onStemTimeChange={e =>
							handleInputChange('stemTime', e.target.value)
						}
					/>

					{/* Equipment Info */}
					<div
						className={`text-sm mb-1 ${
							isDarkMode ? 'text-gray-400' : 'text-gray-600'
						}`}
					>
						<span className="font-medium">Equipment:</span> {trailerDetails}
						<span className="ml-2 font-medium">Loading Type:</span>{' '}
						{loadingType || 'Drop & Hook'}
					</div>
				</div>

				{/* Footer */}
				<div className={`modal-footer ${isDarkMode ? 'dark' : ''}`}>
					<button
						onClick={handleAnimatedClose}
						disabled={isConfirming}
						className={`btn-secondary ${isDarkMode ? 'dark' : ''}`}
					>
						Cancel
					</button>
					<button
						onClick={handleConfirm}
						disabled={isConfirming}
						className="btn-primary"
					>
						{isConfirming ? 'Confirming...' : 'Confirm'}
					</button>
				</div>
			</div>
		</div>
	)
}

export default PostATruckModal
